import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import { MidButton } from '@this/components/shared/atoms/button';
import ContentBody from '@this/components/shared/atoms/content_body';
import { Link } from '@this/shared/ui/navigations/link';

import Tooltip from '@this/shared/tooltip/tooltip';
import AlertArea from '@this/components/reserve_trip/select/alert_area/alert_area';
import { Modal, ModalBody, ModalHeader } from '@this/shared/ui/feedbacks/modal';
import type SearchQuery from '../../../domain/search_query';
import type SearchQueryItem from '../../../domain/search_query_item';
import type { Airline } from '../../../domain/select_store';
import type User from '../../../domain/user/user';

import { useSelectContext } from './context/SelectContext';

import SelectDefaultArea from './default_area/default_area';
import SelectListArea from './list_area/list_area';
import SelectSearchArea from './search_area/search_area';
import SelectOutlineWrapperTemplate from './outline_wrapper/outline_wrapper.template';
import SelectOutlineWrapperTop from './outline_wrapper/outline_wrapper.top';

import ArrangementRequestForm from './arrangement_request_form/arrangement_request_form';

interface Props {
  showArrangementRequestForm: boolean;
  showSearchBox: boolean;
  additionalSearchErrors?: { [k in string]: string };
  serviceId: number;
  user: User | null;
  availableOptions: string[];

  handleAirline: (airline: Airline) => () => void;
  handleSelectItem: () => void;
  handleAddItem: (item_type: 'transport' | 'hotel', e: React.MouseEvent<HTMLElement>) => void;
  handleAdditionalSearch: (queryItem: SearchQueryItem, e: React.FormEvent<HTMLFormElement>) => void;
  handleReSearch: (query: SearchQuery) => void;
  handleRemoveItem: () => void;
  handleToggleArrangementRequestForm: () => void;
  toggleShowSearchBox: () => void;
  handleOpenShowSearchBox: () => void;
}

const SelectPcTemplate: React.FC<Props> = ({
  showArrangementRequestForm,
  serviceId,
  user,
  availableOptions,
  showSearchBox,
  additionalSearchErrors,

  handleAirline,
  handleSelectItem,
  handleAddItem,
  handleAdditionalSearch,
  handleReSearch,
  handleRemoveItem,
  handleToggleArrangementRequestForm,
  toggleShowSearchBox,
  handleOpenShowSearchBox
}) => {
  const {
    store,
    repository,
    query,
    selectErrors,
    submitting,
    isAnyItemLoading,
    handleSelectType,
    handleNextButtonClick
  } = useSelectContext();
  const isButtonDisabled = submitting || isAnyItemLoading;

  const leftColumnRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const [listMaxHeight, setListMaxHeight] = useState<number | null>(null);
  const isListOpen = store?.currentTab !== 'none';
  const currentTab = store?.currentTab;

  useEffect(() => {
    if (!isListOpen) {
      setListMaxHeight(null);
      return;
    }

    const updateListMaxHeight = () => {
      const leftHeight = leftColumnRef.current?.offsetHeight ?? 0;
      const rightTop = rightColumnRef.current?.getBoundingClientRect().top ?? 0;
      const viewportMaxHeight = Math.max(
        0,
        Math.min(window.innerHeight - rightTop - 48, window.innerHeight * 0.66)
      );
      setListMaxHeight(Math.max(leftHeight, viewportMaxHeight));
    };

    updateListMaxHeight();
    const rafId = requestAnimationFrame(updateListMaxHeight);

    const leftColumn = leftColumnRef.current;
    const observer = leftColumn ? new ResizeObserver(updateListMaxHeight) : null;
    if (leftColumn && observer) {
      observer.observe(leftColumn);
    }

    window.addEventListener('resize', updateListMaxHeight);
    window.addEventListener('scroll', updateListMaxHeight, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
      window.removeEventListener('resize', updateListMaxHeight);
      window.removeEventListener('scroll', updateListMaxHeight);
    };
  }, [isListOpen, currentTab]);

  const rightColumnStyle = useMemo(() => {
    if (!isListOpen || listMaxHeight == null) return undefined;

    return {
      maxHeight: listMaxHeight,
      minHeight: 0
    };
  }, [isListOpen, listMaxHeight]);

  if (store == null || repository == null) {
    return null;
  }

  return (
    <SelectPc className="select-pc">
      <Wrap>
        <Body>
          <BodyIn>
            <SelectSearchArea
              query={query}
              user={user}
              trip={store.result.getReservingTrip()}
              selectErrors={selectErrors}
              submitting={isButtonDisabled}
              store={store}
              handleNextButtonClick={handleNextButtonClick}
              handleReSearch={handleReSearch}
              availableOptions={availableOptions}
            />
            <AlertArea result={store.result} />
            <Content>
              <Left ref={leftColumnRef}>
                <SelectOutlineWrapperTop
                  store={store}
                  serviceId={serviceId}
                  handleSelectType={handleSelectType}
                  handleAirline={handleAirline}
                  width={100}
                />
                <SelectOutlineWrapperTemplate
                  query={query}
                  store={store}
                  selectErrors={selectErrors}
                  packageErrors={store.packageErrors[store.result.airline]}
                  handleSelectItem={handleSelectItem}
                  handleAddItem={handleAddItem}
                  handleRemoveItem={handleRemoveItem}
                  handleAdditionalSearch={handleAdditionalSearch}
                  additionalSearchErrors={additionalSearchErrors}
                  handleNextButtonClick={handleNextButtonClick}
                  handleOpenShowSearchBox={handleOpenShowSearchBox}
                  submitting={isButtonDisabled}
                />
                {utils.dig(store, 'user', 'organization', 'arrangement_request_enable?') ? (
                  <RequestWrapper>
                    <ReserveButton
                      className={`select-pc__reserve-button ${isButtonDisabled ? 'loading' : ''}`}
                      onClick={handleNextButtonClick}
                      style={isButtonDisabled ? { pointerEvents: 'none' } : undefined}
                    >
                      予約確認画面へ
                    </ReserveButton>
                    <RequestText>
                      <p>
                        お探しの結果が見つからない場合は
                        <Link
                          onClick={handleToggleArrangementRequestForm}
                          style={{ borderBottom: '1px solid', cursor: 'pointer' }}
                        >
                          リクエストフォーム
                        </Link>
                        からご依頼ください
                      </p>
                      <Tooltip>
                        <p>
                          ご希望の検索結果が出てこない場合は、こちらのフォームからご依頼ください
                          <br />
                          <span data-wovn-ignore>{`（手数料: ${utils.dig(
                            store,
                            'user',
                            'organization',
                            'arrangement_request_setting',
                            'fee'
                          )}）`}</span>
                          円
                        </p>
                      </Tooltip>
                    </RequestText>
                  </RequestWrapper>
                ) : null}
              </Left>
              <Right ref={rightColumnRef} style={rightColumnStyle}>
                <SelectDefaultArea
                  isOrganization
                  show={store.currentTab === 'none'}
                  serviceId={serviceId}
                  sp={false}
                />
                {store.result.items.map((item, i) => (
                  <SelectListArea
                    key={item.index}
                    icon={String(i + 1)}
                    repository={repository}
                    store={store}
                    listWrapper={item.elementList}
                    type={item.elementType()}
                    subtype={item.flightIndex}
                    queryItem={query.items[i]}
                    resultItem={item}
                    query={query}
                    serviceId={serviceId}
                    show={store.result.type === 'separate' && store.currentTab === item.index}
                    showSearchBox={showSearchBox}
                    toggleShowSearchBox={toggleShowSearchBox}
                    isPcLayout
                  />
                ))}
                {store.result.packageItems[store.result.airline].map((item, i) => (
                  <SelectListArea
                    key={item.index}
                    icon={String(i + 1)}
                    repository={repository}
                    store={store}
                    listWrapper={item.elementList}
                    type={item.elementType()}
                    subtype={item.flightIndex}
                    queryItem={query.items[i]}
                    resultItem={item}
                    query={query}
                    serviceId={serviceId}
                    show={store.result.type === 'airPackage' && store.currentTab === item.index}
                    direction={i === 0 ? 'out' : i === 2 ? 'home' : undefined}
                    showSearchBox={showSearchBox}
                    toggleShowSearchBox={toggleShowSearchBox}
                    isPcLayout
                  />
                ))}
                {store.result.jrPackageItems.map((item, i) => (
                  <SelectListArea
                    key={item.index}
                    icon={String(i + 1)}
                    repository={repository}
                    store={store}
                    listWrapper={item.elementList}
                    type={item.elementType()}
                    subtype={item.flightIndex}
                    queryItem={query.items[i]}
                    resultItem={item}
                    query={query}
                    serviceId={serviceId}
                    show={store.result.type === 'jrPackage' && store.currentTab === item.index}
                    showSearchBox={showSearchBox}
                    toggleShowSearchBox={toggleShowSearchBox}
                    isPcLayout
                  />
                ))}
              </Right>
            </Content>
          </BodyIn>
        </Body>
        {showArrangementRequestForm && (
          <Modal
            open={showArrangementRequestForm}
            onClose={handleToggleArrangementRequestForm}
            title="リクエストフォーム"
            size="large"
          >
            <ModalHeader>リクエストフォーム</ModalHeader>
            <ModalBody>
              <ArrangementRequestForm
                serviceId={serviceId}
                query={query}
                store={store}
                selectErrors={selectErrors}
                handleSelectItem={handleSelectItem}
                handleAdditionalSearch={handleAdditionalSearch}
                additionalSearchErrors={additionalSearchErrors}
                handleNextButtonClick={handleNextButtonClick}
                handleOpenShowSearchBox={handleOpenShowSearchBox}
                reserveRequestMailNotificationType={
                  store.user?.organization?.reserve_request_mail_notification_type ?? 'togglable'
                }
                shortdistance={false}
              />
            </ModalBody>
          </Modal>
        )}
      </Wrap>
    </SelectPc>
  );
};

const SelectPc = styled.div`
  display: flex;
`;

const Wrap = styled.div`
  display: flex;
  flex-grow: 99999;
  flex-direction: column;
  width: 100%;
  min-height: 0%;
`;

const Body = styled(ContentBody)`
  display: flex;
  flex-direction: column;
  width: 1150px;
  padding: 0 20px;
`;

const BodyIn = styled.div`
  display: flex;
  flex-grow: 99999;
  flex-direction: column;
  min-height: 0%;
  background: #fff;
`;

const Content = styled.div`
  display: flex;
  flex-grow: 99999;
  min-height: 0%;
  padding-top: 20px;
  gap: 20px;
  align-items: flex-start;
`;

const Left = styled.div`
  flex-basis: 360px;
  flex-grow: 10;
`;

const RequestText = styled.div`
  display: flex;
  padding: 10px;

  .tooltip__content {
    right: -60px;
  }
`;

const Right = styled.div`
  display: flex;
  flex-basis: 360px;
  flex-grow: 10;
  flex-direction: column;
  min-height: 0;
`;

const RequestWrapper = styled.div``;

const ReserveButton = styled.div`
  ${MidButton}
  position: relative;
  width: 214px;
  margin: 0 0 10px 10px;
  ${media.sp`
    display: none;
  `}

  &:before {
    content: '';
    position: absolute;
    top: 16px;
    left: 50px;
    border: 5px solid transparent;
    border-left: 5px solid #fff;
  }
`;

export default SelectPcTemplate;
